/**
 * Formats français centralisés (§5 du prompt de finition) : aucun composant ne
 * recompose une date ou un montant à la main. Les VALEURS métier ne sont jamais
 * altérées : centimes et millièmes restent entiers, seul l'affichage change.
 */
import { approxClockDetail, customerTimeFr, formatEuros } from '@myequip/shared';

const NOMBRE = new Intl.NumberFormat('fr-FR', { maximumFractionDigits: 3 });
const MONTANT = new Intl.NumberFormat('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const DATE = new Intl.DateTimeFormat('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric' });
const DATE_LONGUE = new Intl.DateTimeFormat('fr-FR', { weekday: 'long', day: 'numeric', month: 'long' });
const HEURE = new Intl.DateTimeFormat('fr-FR', { hour: '2-digit', minute: '2-digit' });
const JOUR_HEURE = new Intl.DateTimeFormat('fr-FR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' });
const DATE_HEURE = new Intl.DateTimeFormat('fr-FR', {
  day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit',
});

/** Accord d'un groupe nominal affiché avec un nombre (0 et >1 au pluriel). */
export function compteFr(n: number, singulier: string, pluriel = `${singulier}s`): string {
  return `${n} ${n === 1 ? singulier : pluriel}`;
}

/** Montant stocké en centimes → « 1 440,00 € ». */
export function euros(cents: number | string | null | undefined): string {
  if (cents === null || cents === undefined) return '—';
  return formatEuros(Number(cents));
}

/**
 * Montant en centimes dans sa devise (solde d'un compte bancaire) : « 1 234,56 € »,
 * « 1 234,56 $US ». Un code de devise inconnu d'`Intl` s'affiche tel quel après le nombre.
 */
export function montantDevise(cents: number, currency: string): string {
  if (currency === 'EUR') return euros(cents);
  try {
    return new Intl.NumberFormat('fr-FR', { style: 'currency', currency }).format(cents / 100);
  } catch {
    return `${MONTANT.format(cents / 100)} ${currency}`;
  }
}

/** Quantité stockée en millièmes → « 1,5 » (jamais de division visible dans les vues). */
export function quantite(milli: number | string | null | undefined): string {
  if (milli === null || milli === undefined) return '—';
  return NOMBRE.format(Number(milli) / 1000);
}

const POURCENT = new Intl.NumberFormat('fr-FR', { style: 'percent', maximumFractionDigits: 1 });

/** Taux stocké en points de base → « 20 % », « 5,5 % » (TVA, marge, remise, acompte). */
export function pourcentage(bp: number | string | null | undefined): string {
  if (bp === null || bp === undefined) return '—';
  return POURCENT.format(Number(bp) / 10000);
}

/** Durée telle qu'on la dit sur le terrain : « 15 min », « 1 h », « 1 h 30 » (espaces insécables : jamais coupée en fin de ligne). */
export function duree(minutes: number | string | null | undefined): string {
  if (minutes === null || minutes === undefined) return '—';
  const total = Math.round(Number(minutes));
  const h = Math.floor(Math.abs(total) / 60);
  const m = Math.abs(total) % 60;
  const sign = total < 0 ? '−' : '';
  if (h === 0) return `${sign}${m}\u00a0min`;
  return m === 0 ? `${sign}${h}\u00a0h` : `${sign}${h}\u00a0h\u00a0${String(m).padStart(2, '0')}`;
}

export function heures(minutes: number | string | null | undefined): string {
  if (minutes === null || minutes === undefined) return '—';
  return `${NOMBRE.format(Number(minutes) / 60)} h`;
}

/** « 13/07/2026 » — jamais de secondes. */
export function dateFr(value: string | Date | null | undefined): string {
  if (!value) return '—';
  return DATE.format(new Date(value));
}

/** « lundi 13 juillet » (en-têtes d'accueil). */
export function dateLongue(value: string | Date | null | undefined): string {
  if (!value) return '—';
  return DATE_LONGUE.format(new Date(value));
}

/** « 15:22 ». */
export function heure(value: string | Date | null | undefined): string {
  if (!value) return '—';
  return HEURE.format(new Date(value));
}

/** « 13/07/2026 15:22 » — jamais de secondes (défaut relevé sur les portails). */
export function dateHeureFr(value: string | Date | null | undefined): string {
  if (!value) return '—';
  return DATE_HEURE.format(new Date(value));
}

/** « 13/07 15:22 » : l'âge d'une donnée affichée hors ligne. */
export function jourHeureFr(value: string | number | Date | null | undefined): string {
  if (value === null || value === undefined || value === '') return '—';
  return JOUR_HEURE.format(new Date(value)).replace(',', '');
}

/**
 * Rendez-vous lu par un client (portail, suivi de demande) : « 13/07/2026 15:22 »,
 * ou « 13/07/2026 vers 14 h (entre 13 h et 15 h) » quand l'heure est approximative.
 * Le client ne lit jamais une heure exacte que l'entreprise n'a pas donnée.
 */
export function rendezVousClientFr(
  value: string | Date | null | undefined,
  approxMinutes: number | null | undefined,
): string {
  if (!value) return '—';
  return approxMinutes ? `${dateFr(value)} ${customerTimeFr(value, approxMinutes)}` : dateHeureFr(value);
}

/**
 * Rendez-vous lu dans l'application : « 13/07/2026 15:22 », ou
 * « 13/07/2026 vers 14:00 (± 1 h) » quand l'heure est approximative.
 */
export function dateHeureApproxFr(
  value: string | Date | null | undefined,
  approxMinutes: number | null | undefined,
): string {
  if (!value) return '—';
  return approxMinutes ? `${dateFr(value)} ${approxClockDetail(value, approxMinutes)}` : dateHeureFr(value);
}
